import React, { memo, useEffect, useMemo, useRef, useState } from "react";
import styles from "./Sidebar.module.scss";
import { generalData as sidebarItems } from "Shared/Constants/sidebar";
import cn from "classnames";
import { useSelector } from "react-redux";
import { selectPersonalGeneral } from "Redux/CompanyProps/slice";
import { useAppDispatch } from "Redux/store";
import { getPersonalGeneral } from "Redux/CompanyProps/reducer";
import { NewLogoIcon } from "Assets";
import SidebarHeaderItem from "./SidebarHeaderItem";
import { Link, NavLink } from "react-router-dom";
import { SETTINGS_ROOT } from "Shared/Constants/routes";
import {
  selectDefaultRoute,
  selectPermissions,
  selectPermissionsLoading,
} from "Redux/Role/slice";
import { PERMISSIONS } from "Shared/Types/permissions";
import {
  ChevronLeftRounded,
  ChevronRightRounded,
  HelpOutlineRounded,
  SearchRounded,
  SettingsOutlined,
} from "@mui/icons-material";
import SafeImage from "Components/UI/SafeImage/SafeImage";
import { SUPPORT_MAILTO } from "Shared/Constants/app";

type SidebarProps = { collapsed: boolean; onToggleCollapsed: () => void };

const SidebarNew: React.FC<SidebarProps> = ({
  collapsed,
  onToggleCollapsed,
}) => {
  const dispatch = useAppDispatch();
  const [query, setQuery] = useState("");
  const searchRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (collapsed) setQuery("");
  }, [collapsed]);

  const companyInfo = useSelector(selectPersonalGeneral);
  const permissions = useSelector(selectPermissions);
  const defaultRoute = useSelector(selectDefaultRoute);
  const loadingPermissions = useSelector(selectPermissionsLoading);

  const filteredSidebarItems = useMemo(() => {
    if (!permissions) return [];
    if (permissions.has_full_access) return sidebarItems;
    return sidebarItems
      .map((item) => {
        const filteredSubMenu = item.subMenu
          .map((subItem) => {
            // To assign Array with multiple types
            let subsubItems =
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              subItem.subMenu.filter(
                (subsubItem: any) =>
                  (!subsubItem.modules ||
                    subsubItem.modules.some((module: any) => permissions.modules.has(module))) &&
                  (!subsubItem.permissions ||
                    subsubItem.permissions.some((permission: PERMISSIONS) =>
                      permissions.available_permissions.has(permission),
                    )),
              ) || [];
            let defaultAccess = true;
            if (
              subItem.modules &&
              subItem.modules.every(
                (module) => !permissions.modules.has(module),
              )
            ) {
              subsubItems = [];
              defaultAccess = false;
            }

            return {
              ...subItem,
              subMenu: subsubItems,
              hasAccess: !!subsubItems?.length || defaultAccess,
            };
          })
          .filter((subItem) => subItem.hasAccess);
        return {
          ...item,
          hasAccess: !!filteredSubMenu.length,
          subMenu: filteredSubMenu,
        };
      })
      .filter((item) => item.hasAccess);
  }, [permissions]);

  const visibleItems = useMemo(() => {
    const term = query.trim().toLowerCase();
    if (!term) return filteredSidebarItems;
    return filteredSidebarItems
      .map((section) => ({
        ...section,
        subMenu: section.subMenu
          .map((item) => {
            if (
              section.title.toLowerCase().includes(term) ||
              item.title.toLowerCase().includes(term)
            )
              return item;
            return {
              ...item,
              subMenu: item.subMenu.filter((child: { title: string }) =>
                child.title.toLowerCase().includes(term),
              ),
            };
          })
          .filter(
            (item) =>
              section.title.toLowerCase().includes(term) ||
              item.title.toLowerCase().includes(term) ||
              item.subMenu.length > 0,
          ),
      }))
      .filter((section) => section.subMenu.length > 0);
  }, [filteredSidebarItems, query]);

  useEffect(() => {
    dispatch(getPersonalGeneral());
  }, [dispatch]);

  return (
    <div className={cn(styles.sidebar, collapsed && styles.sidebar_collapsed)}>
      <div className={styles.sidebar__brandRow}>
        <Link
          className={styles.sidebar__brand}
          to={defaultRoute}
          aria-label="Easy Fleet home"
          title="Easy Fleet home"
        >
          <span className={styles.sidebar__brandMark}>
            <SafeImage
              src={companyInfo?.logo?.file}
              alt=""
              fallback={<NewLogoIcon aria-hidden="true" />}
            />
          </span>
          {!collapsed && (
            <span className={styles.sidebar__brandCopy}>
              <strong>Easy Fleet</strong>
              <small>{companyInfo?.account_name || "Fleet workspace"}</small>
            </span>
          )}
        </Link>
        <button
          type="button"
          className={styles.sidebar__collapseButton}
          onClick={onToggleCollapsed}
          aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
          aria-expanded={!collapsed}
          title={collapsed ? "Expand navigation" : "Collapse navigation"}
        >
          {collapsed ? <ChevronRightRounded /> : <ChevronLeftRounded />}
        </button>
      </div>

      <div className={styles.sidebar__searchWrap}>
        {collapsed ? (
          <button
            type="button"
            className={styles.sidebar__searchCompact}
            onClick={() => {
              onToggleCollapsed();
              requestAnimationFrame(() => searchRef.current?.focus());
            }}
            aria-label="Open navigation search"
            title="Search navigation"
          >
            <SearchRounded />
          </button>
        ) : (
          <label className={styles.sidebar__search}>
            <SearchRounded aria-hidden="true" />
            <input
              ref={searchRef}
              type="search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === "Escape") setQuery("");
              }}
              placeholder="Search menu"
              aria-label="Search navigation"
            />
          </label>
        )}
      </div>

      <div className={styles.sidebar__scrollArea}>
        {loadingPermissions ? (
          <div
            className={styles.sidebar__loading}
            aria-label="Loading navigation"
          >
            <span />
            <span />
            <span />
            <span />
            <span />
          </div>
        ) : visibleItems.length ? (
          <div className={styles.sidebar__general}>
            {visibleItems.map((item) => (
              <SidebarHeaderItem
                key={item.title}
                title={item.title}
                subMenu={item.subMenu}
                icon={item.icon}
                collapsed={collapsed}
                searching={!!query.trim()}
              />
            ))}
          </div>
        ) : (
          <p className={styles.sidebar__empty}>
            {query ? "No matching pages" : "Navigation is unavailable"}
          </p>
        )}
      </div>

      <div className={styles.sidebar__footer}>
        <NavLink
          to={SETTINGS_ROOT}
          className={({ isActive }) =>
            cn(styles.sidebar__settings, isActive && styles.sidebar__settings_active)
          }
          aria-label="Settings"
          title="Settings"
        >
          <SettingsOutlined aria-hidden="true" />
          {!collapsed && <span>Settings</span>}
        </NavLink>
        {collapsed ? (
          <a
            href={SUPPORT_MAILTO}
            className={styles.sidebar__helpCompact}
            aria-label="Contact support"
            title="Contact support"
          >
            <HelpOutlineRounded />
          </a>
        ) : (
          <div className={styles.sidebar__helpCard}>
            <div className={styles.sidebar__helpTitle}>
              <HelpOutlineRounded /> <strong>Need support?</strong>
            </div>
            <p>Get in touch with our team.</p>
            <a href={SUPPORT_MAILTO}>Contact us</a>
          </div>
        )}
      </div>
    </div>
  );
};
export default memo(SidebarNew);
